import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toastr from "toastr";
import { useOutletContext } from "react-router-dom";

import { requirementsFor, isComplete, FIELD_SECTIONS } from "./finishRules";

/**
 * Finish the people the import could not place.
 *
 * The import refuses any status PINE would not let a human set without more
 * information - a CV, a date, a panel. Those people used to be listed with a
 * link out to PINE's own status form, one browser tab each. This collects the
 * same things in one place.
 *
 * ONE ROW PER PERSON, GROUPED BY WHAT THAT PERSON NEEDS. Everyone who needs
 * only a CV sits together; everyone who needs a CV and a date sits together
 * below them, with both inputs on the same row.
 *
 * The first attempt grouped by field instead - all the CVs, then all the dates -
 * and it was built and looked at. Someone needing three things appeared three
 * times, and whether they were finished was spread across three sections. One
 * row per person is worse for doing twenty CVs in a burst and better for
 * knowing where you are, which is the thing people actually ask.
 *
 * NOTHING PARTIAL IS EVER WRITTEN. A person is sent only when everything their
 * status demands is present. Close this screen halfway and nothing happened -
 * candidates the import created stay at Prospective / New, and candidates
 * already on the role keep the status they had. There is no state where a file
 * exists against a status that was never set.
 *
 * THIS SCREEN IS THE ENFORCEMENT. update_candidate_status_data accepts whatever
 * it is given - a candidate can be set to Offer Accepted with no resume, no
 * dossier and no compensation and it will save. finishRules.js is what stands
 * between a spreadsheet and a record a human could never have created.
 */

const ACCENT = "rgb(var(--primary-rgb, 210, 105, 30))";

function FinishBlocked({
  role_id,
  people,
  roleContext,
  pineStatuses,
  currencies = [],
  onBack,
  onClose,
}) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { token } = useOutletContext();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Loaded once for the whole screen, not per person.
  const [roleWantsDossier, setRoleWantsDossier] = useState(false);
  const [users, setUsers] = useState([]);
  const [interviewers, setInterviewers] = useState([]);
  // No timezone list. The only status that wants one is 54, which needs the
  // contribution form and is never finished here - so loading it would be a
  // request whose answer can never be used.

  // What PINE already holds, keyed by candidate id.
  const [hasResume, setHasResume] = useState({});
  // The actual CV on file, keyed by candidate id, with the search it came from.
  const [resume, setResume] = useState({});
  // Who has asked to attach a newer CV although one already exists.
  const [showUpload, setShowUpload] = useState({});
  const [hasComp, setHasComp] = useState({});
  // What PINE knows about each person - name, designation, company. Empty for
  // anyone the import just created, until the sync runs.
  const [details, setDetails] = useState({});

  // What the user has filled in, keyed by LinkedIn URL - the only identifier
  // every person is guaranteed to have, since a freshly imported candidate has
  // no name until the sync runs.
  const [values, setValues] = useState({});

  const [saving, setSaving] = useState(false);
  // url -> "saved" | an error string. A saved person stays on screen, ticked,
  // so a wrongly attached file can still be seen. Replacing it is not built
  // here, but removing the row would make it impossible later.
  const [outcome, setOutcome] = useState({});

  /* ---- load everything the screen needs ------------------------------- */

  // The parent builds `people` fresh on every render, so depending on the array
  // itself re-runs this effect forever: load -> setState -> render -> new array
  // -> load again. The cleanup sets cancelled, which skips setLoading(false),
  // so it sits on "Loading…" and quietly hammers the backend in a loop.
  //
  // The ids are what the effect actually cares about, and they are stable.
  const idsKey = people.map((row) => row.candidateId).join(",");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      const ids = [
        ...new Set(idsKey.split(",").filter(Boolean).map(Number)),
      ];

      try {
        const [role, activeUsers, basic, existing] = await Promise.all([
          axios.get(`${API_BASE_URL}/roles/fetch_role_data/${role_id}`, auth),
          axios.get(`${API_BASE_URL}/employeeinfos/getActiveUser`, auth),
          axios.get(
            `${API_BASE_URL}/workstation/get_basic_data_edit_status_form/${role_id}`,
            auth,
          ),
          axios.post(
            `${API_BASE_URL}/candidates/candidates_existing_requirements`,
            { candidate_ids: ids, role_id },
            auth,
          ),
        ]);

        if (cancelled) return;

        // fetch_role_data returns { role_data: [row], rolecriterias: [...] } -
        // the role is an ARRAY inside an object, not the object itself.
        setRoleWantsDossier(
          Number(role.data?.role_data?.[0]?.dossier) === 1,
        );

        // Both lists are normalised here rather than at render, because they
        // have different shapes and neither is what it looks like:
        //
        //   getActiveUser  -> { options: [...] }, and each row's `Name` is the
        //                     person while `name` is their DESIGNATION. Reading
        //                     `name` puts job titles in the dropdown, which
        //                     looks plausible and is wrong.
        //   interviewpanels -> firstname / lastname, no name column at all.
        setUsers(
          (activeUsers.data?.options || []).map((row) => ({
            id: row.id,
            label: row.Name,
          })),
        );

        setInterviewers(
          (basic.data?.interview || []).map((row) => ({
            id: row.id,
            label: `${row.firstname || ""} ${row.lastname || ""}`.trim(),
          })),
        );

        setHasResume(existing.data?.has_resume || {});
        setResume(existing.data?.resume || {});
        setHasComp(existing.data?.has_compensation || {});
        setDetails(existing.data?.details || {});
      } catch (err) {
        console.error(err);
        if (!cancelled) setLoadError("Could not load what this screen needs.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [API_BASE_URL, token, role_id, idsKey]);

  /* ---- what each person needs ----------------------------------------- */

  const requirements = useMemo(() => {
    const out = {};

    people.forEach((row) => {
      out[row.url] = requirementsFor(row.wantedStatusId, {
        roleWantsDossier,
        candidateHasResume: Boolean(hasResume[row.candidateId]),
        candidateHasComp: Boolean(hasComp[row.candidateId]),
      });
    });

    return out;
  }, [people, roleWantsDossier, hasResume, hasComp]);

  const completeFor = (row) =>
    isComplete(requirements[row.url] || { fields: [] }, values[row.url] || {});

  /**
   * The fields this person still owes, by their on-screen label.
   *
   * Uses the same emptiness test as isComplete, so what the screen says is
   * missing and what the save button counts can never disagree.
   *
   * This exists because they did disagree in practice, silently. A half-typed
   * datetime-local reports "" to JavaScript while showing digits in the box,
   * and a multi-select nobody quite clicked holds an empty array. Both look
   * filled in and neither is, and the only feedback was a footer saying
   * nothing was ready - naming nobody and no field.
   */
  const missingFor = (row) => {
    const filled = values[row.url] || {};
    return (requirements[row.url]?.fields || [])
      .filter((field) => {
        const value = filled[field];
        if (value === undefined || value === null || value === "") return true;
        if (Array.isArray(value)) return value.length === 0;
        return false;
      })
      .map(
        (field) =>
          FIELD_SECTIONS.find((section) => section.key === field)?.label ||
          field,
      );
  };

  // Anything typed or attached that has not been written. What Done would
  // throw away if it closed without asking.
  const unsavedWork = people.some(
    (row) =>
      outcome[row.url] !== "saved" &&
      Object.values(values[row.url] || {}).some((value) =>
        Array.isArray(value) ? value.length > 0 : Boolean(value),
      ),
  );

  const ready = people.filter(
    (row) => completeFor(row) && outcome[row.url] !== "saved",
  );

  // Saved people stay on screen, ticked, so a wrongly attached file can still
  // be seen. That leaves nothing in `ready` once the last one is written, and
  // the Save button disabled reading "Save 0 people" with no way out but the
  // modal's close icon.
  const savedCount = people.filter(
    (row) => outcome[row.url] === "saved",
  ).length;

  // Nobody is complete and nothing has been written - the state the screen
  // opens in. The button says Done rather than a greyed-out "Save 0 people",
  // and the reason it cannot save sits beside it.
  const nothingReadyYet = ready.length === 0 && savedCount === 0;

  const outstanding = people.reduce(
    (n, row) =>
      n +
      (requirements[row.url]?.fields || []).filter(
        (field) => !(values[row.url] || {})[field],
      ).length,
    0,
  );

  const setValue = (url, field, value) =>
    setValues((prev) => ({ ...prev, [url]: { ...(prev[url] || {}), [field]: value } }));

  /**
   * People grouped by the SET of things they need, not by individual field.
   *
   * Everyone needing only a CV in one group; everyone needing a CV and a date
   * in the next. One row per person, all their inputs on it.
   *
   * Keyed on the sorted field list, so the grouping is by what is actually
   * required rather than by status - two different statuses demanding the same
   * things belong together, because the work is the same.
   */
  const groups = useMemo(() => {
    const byKey = new Map();

    people.forEach((row) => {
      if (requirements[row.url]?.blocked) return; // shown in their own section

      const fields = requirements[row.url]?.fields || [];

      // Someone the import refused who nevertheless needs nothing - status 19
      // is the usual case, which the import blocks and edit_status.jsx does not
      // gate on anything. They are ready the moment the screen opens.
      //
      // They get a group rather than being skipped. Skipping them left four
      // people counted in "Save 5 people" and drawn nowhere, so pressing Save
      // would have changed statuses for people who were never on screen.
      const key = fields.length === 0 ? "" : [...fields].sort().join("+");

      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          // In FIELD_SECTIONS order, so a row's inputs always appear in the
          // same order regardless of which group it landed in.
          sections: FIELD_SECTIONS.filter((section) =>
            fields.includes(section.key),
          ),
          rows: [],
        });
      }

      byKey.get(key).rows.push(row);
    });

    return [...byKey.values()]
      .map((group) => ({
        ...group,
        label:
          group.sections.length === 0
            ? "Ready to set, nothing needed"
            : group.sections.map((section) => section.label).join(", "),
      }))
      // Fewest requirements first - the quickest wins at the top, and the
      // people needing nothing sit above everything.
      .sort((a, b) => a.sections.length - b.sections.length);
  }, [people, requirements]);

  const statusLabel = (id) => {
    const hit = pineStatuses.find((row) => row.minor_id === id);
    return hit ? `${hit.master_status} · ${hit.minor_status}` : `status ${id}`;
  };

  // The minor's real parent. The form has a human pick this from a dropdown and
  // it disagrees with the real parent on 562 of 717,708 rows - deriving it is
  // what a human picking correctly would produce.
  const masterFor = (id) => {
    const hit = pineStatuses.find((row) => row.minor_id === id);
    return hit ? hit.master_id : null;
  };

  /* ---- writing --------------------------------------------------------- */

  const save = async () => {
    setSaving(true);

    // Browser-side counterpart to [status-write] in the backend. Between the
    // two, a status that did not change can be placed exactly: no line here
    // means the screen never sent it, a line here and none there means the
    // request never arrived, and both means the write itself is the problem.
    console.log(
      `[finish] saving ${ready.length}`,
      ready.map((row) => ({
        who: nameOf(row),
        candidate_id: row.candidateId,
        candidate_filter_id: row.candidateFilterId,
        wanted_status: row.wantedStatusId,
        supplying: Object.keys(values[row.url] || {}),
      })),
    );

    // Sequential on purpose. Each request uploads a file, and the backend holds
    // a connection pool of ten - twenty at once buys nothing worth the risk.
    for (const row of ready) {
      const filled = values[row.url] || {};
      const form = new FormData();

      const known = details[row.candidateId] || {};

      // `candidates_id`, plural noun and singular id. That is the name the
      // endpoint destructures; candidate_id is the filterlogs column and
      // sending only that one inserts a null.
      form.append("candidates_id", row.candidateId);
      form.append("candidate_filter_id", row.candidateFilterId);
      form.append("candidate_status_id", row.wantedStatusId);
      form.append("master_status_id", masterFor(row.wantedStatusId) ?? "");
      form.append("role_id", role_id);
      form.append("company_id", roleContext?.company_id ?? "");
      form.append("dutymaster_id", roleContext?.dutymaster_id ?? "");
      form.append("duty_id", roleContext?.duty_id ?? "");
      form.append("linkdin_url", row.url);
      form.append("new_status", "new");

      // NOT optional. update_candidate_status_data opens with
      //   UPDATE candidates SET email = ?, phone = ?, gender = ? WHERE id = ?
      // taking `email || null` for each - so omitting them writes NULL over
      // the candidate's real email and phone. Posting them back unchanged
      // makes that statement a no-op, which is what edit_status.jsx does by
      // accident of having them on its form.
      form.append("email", known.email || "");
      form.append("phone", known.phone || "");
      form.append("gender", known.gender || "");

      // The sheet's own wording, the same text the import wrote for everyone it
      // could place. Comments are required for every status except 28, 29 and
      // 30, and reusing this keeps one person's history consistent with the
      // rest of the import rather than inventing a second voice.
      if (row.comment) form.append("comments", row.comment);

      if (filled.resume) form.append("userfile", filled.resume);
      if (filled.dossier) form.append("userfile1", filled.dossier);
      if (filled.background) form.append("userfile2", filled.background);

      if (filled.dateTime) {
        const [date, time] = String(filled.dateTime).split("T");
        if (date) form.append("date", date);
        if (time) form.append("time", time);
      }
      if (filled.timezone) form.append("timezone_id", filled.timezone);
      if (filled.evaluatedBy?.length)
        form.append("action_by_id_others", filled.evaluatedBy.join(","));
      if (filled.meetingWith?.length)
        form.append("clientName", filled.meetingWith.join(","));
      if (filled.meetingPlace) form.append("clientPlace", filled.meetingPlace);

      try {
        await axios.post(
          `${API_BASE_URL}/candidates/update_candidate_status_data`,
          form,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );
        console.log(`[finish] ok ${nameOf(row)} -> ${row.wantedStatusId}`);
        setOutcome((prev) => ({ ...prev, [row.url]: "saved" }));
      } catch (err) {
        console.error(`[finish] FAILED ${nameOf(row)}`, err);
        // Kept on screen with what they entered, so a retry costs nothing.
        setOutcome((prev) => ({
          ...prev,
          [row.url]:
            err.response?.data?.error ||
            err.response?.data?.message ||
            "The server refused this one.",
        }));
      }
    }

    setSaving(false);
    toastr.success("Saved.");
  };

  /* ---- rendering ------------------------------------------------------- */

  const nameOf = (row) => row.person || `row ${row.number}`;

  /**
   * Open a CV that is already on file.
   *
   * Not an anchor: the file routes sit behind authenticateUser, which reads the
   * Authorization header and nothing else. A link sends none, so the middleware
   * calls token.replace on undefined and the request comes back 500. That is
   * why the View File button on the Edit Status page does nothing.
   */
  const viewResume = async (row) => {
    const file = resume[row.candidateId];
    if (!file || !file.savename) return;

    // The file routes are mounted on the server root, not under /api like every
    // other endpoint (index.js:78). API_BASE_URL ends in /api, so it has to come
    // off or the request 404s. In production the variable is just "/api", which
    // leaves an empty prefix and a correct relative URL.
    const fileBase = String(API_BASE_URL || "").replace(/\/api\/?$/, "");

    try {
      const res = await axios.get(
        `${fileBase}/uploads/resume/${encodeURIComponent(file.savename)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        },
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setOutcome((prev) => ({
        ...prev,
        [row.url]: "That CV could not be opened.",
      }));
    }
  };

  /** The date column holds a day, not a timestamp. */
  const uploadedOn = (value) => {
    if (!value) return "";
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? String(value).slice(0, 10)
      : d.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
  };

  const renderInput = (section, row) => {
    const filled = values[row.url] || {};
    const done = outcome[row.url] === "saved";

    if (section.kind === "file") {
      return (
        <input
          type="file"
          className="form-control form-control-sm"
          style={{ fontSize: "11.5px", maxWidth: "230px" }}
          disabled={done || saving}
          onChange={(e) => setValue(row.url, section.key, e.target.files?.[0] || null)}
        />
      );
    }

    if (section.kind === "datetime") {
      return (
        <input
          type="datetime-local"
          className="form-control form-control-sm"
          style={{ fontSize: "11.5px", maxWidth: "230px" }}
          disabled={done || saving}
          value={filled.dateTime || ""}
          onChange={(e) => setValue(row.url, "dateTime", e.target.value)}
        />
      );
    }

    if (section.kind === "text") {
      return (
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ fontSize: "11.5px", maxWidth: "230px" }}
          placeholder="Where"
          disabled={done || saving}
          value={filled.meetingPlace || ""}
          onChange={(e) => setValue(row.url, "meetingPlace", e.target.value)}
        />
      );
    }

    const options = section.kind === "people" ? users : interviewers;
    const key = section.key;

    return (
      <select
        multiple
        className="form-select form-select-sm"
        style={{ fontSize: "11.5px", maxWidth: "230px", height: "62px" }}
        disabled={done || saving}
        value={filled[key] || []}
        onChange={(e) =>
          setValue(
            row.url,
            key,
            [...e.target.selectedOptions].map((option) => option.value),
          )
        }
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label || `#${option.id}`}
          </option>
        ))}
      </select>
    );
  };

  if (loading) {
    return <div className="text-muted small p-4">Loading…</div>;
  }

  if (loadError) {
    return (
      <div className="alert alert-warning m-3">
        {loadError} These people can still be finished from their profile in
        PINE.
      </div>
    );
  }

  return (
    <>
      <div className="d-flex align-items-baseline gap-2 mb-1">
        <div style={{ fontSize: "19px", fontWeight: 700, color: "#1c1917" }}>
          Finish these
        </div>
        <div className="ms-auto" style={{ fontSize: "12px", color: "#78716b" }}>
          {people.length} {people.length === 1 ? "person" : "people"} ·{" "}
          {outstanding} {outstanding === 1 ? "thing" : "things"} left
        </div>
      </div>

      <div style={{ fontSize: "11.5px", color: "#8a837c", marginBottom: "14px" }}>
        Nobody is saved until everything they need is filled in. Close this and
        nothing changes — they stay exactly as the import left them.
      </div>

      {groups.map((group) => (
        <div key={group.key} className="mb-4">
          <div className="d-flex align-items-baseline gap-2">
            <div style={{ fontSize: "13px", fontWeight: 700 }}>
              {group.label}
            </div>
            <div style={{ fontSize: "11.5px", color: "#8a837c" }}>
              {group.rows.length}{" "}
              {group.rows.length === 1 ? "person" : "people"}
            </div>
          </div>

          {group.rows.map((row) => {
            const known = details[row.candidateId] || {};

            return (
              <div
                key={row.url}
                className="d-flex align-items-start gap-3"
                style={{ padding: "10px 0", borderBottom: "1px solid #f4f1ed" }}
              >
                <div style={{ minWidth: "230px", maxWidth: "260px" }}>
                  <div style={{ fontSize: "12.5px", color: "#1c1917" }}>
                    {known.name || nameOf(row)}
                    {completeFor(row) && (
                      <span style={{ color: "#3f6b46", marginLeft: "6px" }}>
                        {outcome[row.url] === "saved" ? "saved" : "ready"}
                      </span>
                    )}
                  </div>

                  {/* What PINE holds, so the user knows who they are attaching
                      a file to. Blank for anyone the import just created - the
                      sync has not run, and saying nothing beats inventing. */}
                  {(known.designation || known.company) && (
                    <div style={{ fontSize: "11px", color: "#78716b" }}>
                      {[known.designation, known.company]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  )}

                  <div style={{ fontSize: "11px", color: "#78716b" }}>
                    {statusLabel(row.wantedStatusId)}
                  </div>

                  {/* Named, on the person, in the words the fields beside them
                      use. The footer count said "nothing is ready" and left
                      the user to work out who and what - which is how an
                      uploaded CV got thrown away. */}
                  {!requirements[row.url]?.blocked &&
                    outcome[row.url] !== "saved" &&
                    missingFor(row).length > 0 && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#8a5a1b",
                          marginTop: "2px",
                        }}
                      >
                        waiting on: {missingFor(row).join(", ")}
                      </div>
                    )}

                  <div style={{ fontSize: "10.5px", marginTop: "2px" }}>
                    <a href={row.url} target="_blank" rel="noreferrer">
                      {row.sheet ? `${row.sheet} · row ${row.number}` : "profile"}
                    </a>
                  </div>

                  {resume[row.candidateId] && (
                    <div
                      style={{
                        marginTop: "5px",
                        fontSize: "10.5px",
                        // The whole block in the accent, not just the line
                        // about where the file came from. A CV already on
                        // file changes what this person needs, so it should
                        // read as one statement rather than a grey note with
                        // one coloured sentence in it.
                        color: ACCENT,
                        fontWeight: 600,
                      }}
                    >
                      <div>
                        CV on file · {resume[row.candidateId].filename || "no name"}
                        {resume[row.candidateId].date
                          ? ` · ${uploadedOn(resume[row.candidateId].date)}`
                          : ""}
                      </div>

                      {/* The point of the change. A CV from another search
                          satisfies PINE's rule, and used to do so silently.
                          Amber for an earlier search because it is worth a
                          look; green for this role because nothing needs
                          doing. */}
                      <div
                        style={{
                          color: resume[row.candidateId].from_this_role
                            ? "#3f6b46"
                            : ACCENT,
                          fontWeight: 600,
                        }}
                      >
                        {resume[row.candidateId].from_this_role
                          ? "Uploaded for this role."
                          : `Found on an earlier search${
                              resume[row.candidateId].role_name
                                ? ` — ${resume[row.candidateId].role_name}`
                                : ""
                            }.`}
                      </div>

                      {/* What happens if the user does nothing, which was the
                          one thing this block never said. */}
                      <div>This CV will be used unless you add a newer one.</div>

                      <div className="d-flex gap-2" style={{ marginTop: "3px" }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          style={{ fontSize: "10.5px" }}
                          onClick={() => viewResume(row)}
                        >
                          View
                        </button>

                        {!showUpload[row.url] && (
                          <button
                            type="button"
                            className="btn btn-sm btn-link p-0"
                            style={{ fontSize: "10.5px" }}
                            onClick={() =>
                              setShowUpload((prev) => ({ ...prev, [row.url]: true }))
                            }
                          >
                            Add one for this role
                          </button>
                        )}
                      </div>

                      {/* Adds, never replaces - the insert carries this role_id
                          and the older file stays where it is. */}
                      {showUpload[row.url] && (
                        <input
                          type="file"
                          className="form-control form-control-sm"
                          style={{ fontSize: "11px", marginTop: "4px" }}
                          onChange={(e) =>
                            setValue(row.url, "resume", e.target.files?.[0] || null)
                          }
                        />
                      )}
                    </div>
                  )}

                  {outcome[row.url] && outcome[row.url] !== "saved" && (
                    <div style={{ fontSize: "11px", color: "#b91c1c" }}>
                      {outcome[row.url]}
                    </div>
                  )}
                </div>

                {/* Everything this person needs, side by side on their row. */}
                <div className="d-flex flex-wrap gap-2 ms-auto">
                  {group.sections.map((section) => {
                    // Whether THIS field has registered, tested exactly as the
                    // save counts it. A datetime-local that is half typed
                    // looks filled and reports "" - the tick is the only way
                    // to tell those two apart from the outside.
                    const entered = !missingFor(row).includes(section.label);

                    return (
                      <div key={section.key}>
                        <div
                          style={{
                            fontSize: "10px",
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                            color: entered ? "#3f6b46" : "#a8a29b",
                            marginBottom: "2px",
                          }}
                        >
                          {section.label.toUpperCase()}
                          {entered && " ✓"}
                        </div>
                        {renderInput(section, row)}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* People this screen cannot finish at all - a status needing the
          contribution form, or compensation PINE has not got. They keep their
          link out rather than being silently dropped. */}
      {(() => {
        const cannot = people.filter((row) => requirements[row.url]?.blocked);
        if (cannot.length === 0) return null;

        return (
          <div className="mb-3">
            <div style={{ fontSize: "13px", fontWeight: 700 }}>
              Cannot be finished here
            </div>
            {cannot.map((row) => (
              <div
                key={row.url}
                style={{ padding: "6px 0", borderBottom: "1px solid #f4f1ed" }}
              >
                <div className="d-flex align-items-baseline gap-3">
                  <div style={{ fontSize: "12.5px" }}>{nameOf(row)}</div>
                  <div style={{ fontSize: "11px", color: "#8a5a1b" }}>
                    {requirements[row.url].blocked}
                  </div>
                  <a
                    className="btn btn-sm btn-outline-primary ms-auto"
                    style={{ fontSize: "11px" }}
                    href={`/candidates/edit_status/${role_id}/${row.candidateId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Set status
                  </a>
                </div>

              </div>
            ))}
          </div>
        );
      })()}

      <div className="d-flex gap-2 pt-3 border-top">
        <button
          type="button"
          className="btn btn-outline-secondary"
          disabled={saving}
          // Back unmounts this screen, so it discards exactly as much as Done
          // does. Same question.
          onClick={() => {
            if (
              unsavedWork &&
              !window.confirm(
                "Nothing here has been saved yet. Go back and what you have " +
                  "filled in is lost.",
              )
            ) {
              return;
            }
            onBack();
          }}
        >
          Back
        </button>
        {/* Why nothing can be saved yet, next to the button that cannot do it.
            "Save 0 people", greyed out, said what the button could not do and
            never why - and the reason is always the same one: nobody on screen
            has everything their status needs. */}
        {nothingReadyYet && (
          <div
            className="ms-auto d-flex align-items-center"
            style={{ fontSize: "12px", color: "#78716b", maxWidth: "430px" }}
          >
            Nothing is ready to save yet — see{" "}
            <span style={{ color: "#8a5a1b", fontWeight: 600 }}>
              waiting on
            </span>{" "}
            under each name for what is still missing.
          </div>
        )}

        {ready.length === 0 ? (
          <button
            type="button"
            className={`btn ${
              savedCount > 0 ? "btn-primary" : "btn-outline-secondary"
            } ${nothingReadyYet ? "ms-2" : "ms-auto"}`}
            // Always clickable. Closing changes nothing - that is the promise
            // at the top of this screen - so there is no reason to trap
            // anyone here behind a button they cannot press.
            style={savedCount > 0 ? { background: ACCENT } : undefined}
            disabled={saving}
            onClick={() => {
              // Nobody is complete, so Done writes nothing - but the user may
              // have attached a CV and typed a date and be one unnoticed
              // empty field away from saving. Closing silently threw all of
              // that away, which is exactly how this screen lost an upload.
              if (
                unsavedWork &&
                !window.confirm(
                  "Nothing here has been saved yet.\n\n" +
                    "Each person needs everything their status asks for before " +
                    "PINE will take them — the missing fields are listed under " +
                    "their name. Close now and what you have filled in is lost.",
                )
              ) {
                return;
              }
              onClose && onClose();
            }}
          >
            Done
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary ms-auto"
            style={{ background: ACCENT }}
            disabled={saving}
            onClick={save}
          >
            {saving
              ? "Saving…"
              : `Save ${ready.length} ${ready.length === 1 ? "person" : "people"}`}
          </button>
        )}
      </div>
    </>
  );
}

export default FinishBlocked;
